/* eslint-disable react-hooks/exhaustive-deps */
import DataTable from "../components/common/DataTable";
import HeaderInfo from "../components/common/HeaderInfo";
import { Search } from "../components/icons";
import itmImg from "../assets/item-thumbnail.png";
import LowStockModal from "../components/modals/LowStockModal";
import { useEffect, useState } from "react";
import {
  useGetCampusesQuery,
  useGetCategoriesQuery,
  useGetCampusInventoryQuery
} from "../store/api"; 
import jsPDF from "jspdf"; 
import autoTable from "jspdf-autotable"; 
import { Download } from "lucide-react"; 
import toast from "react-hot-toast";

export default function AllInventories() {
  const [lowStockOpen, setLowStockOpen] = useState(false);
  const [selectedCampusId, setSelectedCampusId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(""); 
  const [fromDate, setFromDate] = useState(""); 
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCategory, selectedCampusId, fromDate, toDate, limit]);

  const { data: campusesData } = useGetCampusesQuery(1);
  const campusOptions = campusesData?.data?.campuses || [];

  useEffect(() => {
    if (campusOptions.length > 0 && !selectedCampusId) {
      setSelectedCampusId(campusOptions[0]._id);
    }
  }, [campusOptions, selectedCampusId]);

  const { data: categoriesData } = useGetCategoriesQuery(1);
  const categories = categoriesData?.data?.categories || [];

  const { data, isLoading, isFetching } = useGetCampusInventoryQuery(
    {
      campusId: selectedCampusId,
      page,
      limit,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(selectedCategory && { category: selectedCategory }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate })
    },
    { skip: !selectedCampusId }
  );

  const apiRows = data?.data?.data || data?.data || [];
  const total = data?.total || data?.data?.total || apiRows.length;
  const totalPages = limit === "All" ? 1 : Math.max(1, Math.ceil(total / limit));

  const handleGenerateReport = () => {
    if (apiRows.length === 0) {
      toast.error("No data available to generate report");
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(18);
    const campusName = campusOptions.find(c => c._id === selectedCampusId)?.name || "Campus";
    doc.text(`${campusName} Inventory Report`, 14, 22);

    const tableColumn = ["Item Name", "Category", "Stock Level", "Last Sync"];
    const tableRows = apiRows.map((r) => {
      const itemName = r?.itemName || r?.item?.name || r?.itemId?.name || "N/A";
      const categoryName = r?.itemCategory || r?.category?.name || r?.itemId?.itemCategory?.name || "N/A";
      const stock = Number(r?.stock) || 0;
      const dateStr = r?.createdAt ? new Date(r.createdAt).toLocaleDateString("en-GB") : "N/A";
      return [itemName, categoryName, stock, dateStr];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      theme: "striped",
    });

    doc.save(`Admin_Campus_Inventory_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const columns = [
    { key: "productInfo", label: "Product Info", className: "col-span-3" },

    { key: "cat", label: "Category", className: "col-span-3 text-center" },
    { key: "stock", label: "Stock Level", className: "col-span-4 text-center" },
    { key: "sync", label: "Last Sync", className: "col-span-2 text-center" },
  ];
  const renderRow = (r, idx) => {
    const itemName = r?.itemName || r?.item?.name || r?.itemId?.name || "N/A";
    const itemImage = r?.itemPicture || r?.item?.itemPicture?.secure_url || r?.itemId?.itemPicture?.secure_url || itmImg;
    const categoryName = r?.itemCategory || r?.category?.name || r?.itemId?.itemCategory?.name || "N/A";
    const stock = Number(r?.stock) || 0;
    const dateStr = r?.createdAt ? new Date(r.createdAt).toLocaleDateString("en-GB") : "N/A";
    const refStr = r?._id ? `Ref: ${r._id.slice(-6)}` : "";

    return (
      <div
        key={r._id || idx}
        className={`bg-[#EEEEEE40] ${idx === apiRows.length - 1 ? "pb-28" : ""}`}
      >
        <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5] ">
          <div className="col-span-3 flex items-center gap-[14px] text-black">
            <div className="w-[53px] h-[42px] overflow-hidden">
              <img
                src={itemImage}
                alt={itemName}
                className="w-full h-full object-cover"
              />
            </div>

            <p className="text-black capitalize">{itemName}</p>
          </div>

          <div className="col-span-3 flex justify-center">
            <div className="w-max rounded-[10px] py-0.5 px-2.5 bg-[#D6EAFE] text-[#2C765C] text-[10px] leading-[13px] font-bold uppercase">
              {categoryName}
            </div>
          </div>

          <div className="col-span-4 text-center">
            <div
              className={`${stock === 0 ? "bg-[#FFE1C7] text-[#CA0707] px-[21px]" : "bg-[#E5FFF6] px-[17px] text-[#2C765C]"} inline-block py-1 rounded-full font-bold text-xs`}
            >
              {stock} <span className="font-normal">Units</span>
            </div>
            {stock === 0 && (
              <p className="mt-[3px] leading-4 text-[10px] text-[#E32F2F]">
                Restock Required
              </p>
            )}
          </div>

          <div className="col-span-2 text-center">
            <p className="text-[#000] leading-4 text-xs">{dateStr}</p>
            <p className="mt-1 text-[#929292] text-[10px] leading-4">{refStr}</p>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="pt-14 pl-8 pr-12 min-h-screen">
      <div className="flex justify-between items-start">
        <HeaderInfo
          title="Campus Inventory Explorer"
          desc="Manage localized stock and current branch availability"
        />
        <div className="flex gap-3">
          <button
            onClick={() => setLowStockOpen(true)}
            disabled={!selectedCampusId}
            className="flex items-center gap-2 bg-[#6D4E8A] text-white px-4 py-2 rounded-[8px] text-sm font-medium hover:bg-[#5a3f75] transition-colors disabled:opacity-50"
          >
            View Low Stock
          </button>
          <button
            onClick={handleGenerateReport}
            className="flex items-center gap-2 bg-[#279B9F] text-white px-4 py-2 rounded-[8px] text-sm font-medium hover:bg-[#1f7a7d] transition-colors"
          >
            <Download size={16} /> Generate Report
          </button>
        </div>
      </div>

      <div className="rounded-[10px] border border-[#D9D9D9] mt-4 pt-6 ">
        <div className="pl-5 pr-6">
          <div className=" border border-[#D9D9D9] rounded-[10px] py-4 pl-6 pr-4  flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center flex-1 min-w-[250px] gap-2 py-3 px-4 bg-[#EEEEEE40] rounded-[10px]">
              <Search />
              <input
                type="text"
                placeholder="Search assets by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent outline-none text-black placeholder:text-[#878686]"
              />
            </div>

            <div className="flex flex-wrap justify-center gap-3 w-full md:w-auto">
              <select
                value={selectedCampusId}
                onChange={(e) => setSelectedCampusId(e.target.value)}
                className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-black outline-none text-sm max-w-[250px] min-w-[180px] font-medium truncate"
              >
                {campusOptions.map((opt) => (
                  <option key={opt._id} value={opt._id}>{opt.name}</option>
                ))}
              </select>

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm min-w-[160px] capitalize"
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id} className="capitalize">{c.name}</option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm"
                />
                <span className="text-[#878686]">to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm"
                />
              </div>

              <select
                value={limit}
                onChange={(e) => setLimit(e.target.value === "All" ? "All" : Number(e.target.value))}
                className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value="All">Show All</option>
              </select>

              <button
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                  setSelectedCategory("");
                  setFromDate("");
                  setToDate("");
                  setLimit(10);
                  setPage(1);
                }}
                className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4">
          {isLoading || isFetching ? (
            <p className="px-7 py-6 text-sm text-[#6B7280]">
              Loading inventory...
            </p>
          ) : apiRows.length === 0 ? (
            <p className="px-7 py-6 text-sm text-[#6B7280]">
              No inventory items found.
            </p>
          ) : (
            <DataTable
              columns={columns}
              rows={apiRows}
              renderRow={renderRow}
              page={page}
              totalPages={totalPages}
              onNext={() => setPage((p) => Math.min(p + 1, totalPages))}
              onPrev={() => setPage((p) => Math.max(p - 1, 1))}
            />
          )}
        </div>
      </div>

      <LowStockModal
        open={lowStockOpen}
        onClose={() => setLowStockOpen(false)}
        type="admin"
        campusId={selectedCampusId}
      />
    </div>
  );
}
